import React, { useState, useEffect, useMemo, useContext, useRef } from 'react'
import { ConfigContext } from 'contexts/ConfigContext'
import { TextField } from '@material-ui/core'
import cloneDeep from 'clone-deep'
import { Button, Col, Row } from 'react-bootstrap'
import objectPath from 'object-path'
import DatePickerCustom from '../../DatePicker/DatePickerCustom'
import moment from 'moment'
import FeatherIcon from 'feather-icons-react'
import '../score.css'
import { checkIsWarning } from '../Validation'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import '../../TimePicker/timepicker-custom-styles.css'
import TimePickerCustom from '../../TimePicker/TimePickerCustom'
import ManualTimeInput from 'components/TimePicker/ManualTimeInput'
import dayjs from 'dayjs'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import { calculateValue } from 'designer/component/action/Calculate'
import { DesignContext } from 'contexts/DesignContext'

const inputStyles = {
  textAlign: 'center',
  fontSize: '14px',
}

const AddScore = ({
  fieldKey,
  uischema,
  scoreIndex,
  saveScore,
  cancelScore,
  scoreList,
  isCreateOnly,
  scoreActionCreateOnly = {},
  warningChart,
}) => {
  const designContext = useContext(DesignContext)
  const { jsonForm } = designContext
  const formData = jsonForm.formData
  const { scoreValues = {}, scoreSetting = {}, mapCalculate = {} } = uischema
  const fullConfig = objectPath.get(uischema, 'scoreConfig') || []
  const addScoreRef = useRef(null)

  const scoreConfig = fullConfig.filter(
    ({ isDate, isTime }) => !isDate && !isTime
  )

  const scoreValueStyle = useMemo(() => {
    if (scoreSetting?.scoreWidth) {
      return {
        width: scoreSetting.scoreWidth,
      }
    }
    return {}
  }, [scoreSetting?.scoreWidth])

  const dateChart = useMemo(() => {
    return fullConfig.find(({ isDate, enabled }) => isDate && enabled) || {}
  }, [])

  const timeChart = useMemo(() => {
    return fullConfig.find(({ isTime, enabled }) => isTime && enabled) || {}
  }, [])

  const { times = [], isCurrentTime, isFrequency } = timeChart
  const { isDate } = dateChart
  const dateName = objectPath.get(dateChart, 'name')
  const dateKey = objectPath.get(dateChart, 'key')
  const timeName = objectPath.get(timeChart, 'name')
  const timeKey = objectPath.get(dateChart, 'key')

  const [scores, setScores] = useState([])

  const configContext = useContext(ConfigContext)
  const { layoutType } = configContext.state

  const colorByTheme = useMemo(
    () => (layoutType === 'dark' ? '#adb7be' : '#495057'),
    []
  )

  useEffect(() => {
    if (scoreIndex === -1) {
      let scores = []
      if (!isCurrentTime) {
        times.forEach(({ label }) => {
          let score = cloneDeep(scoreValues)
          objectPath.set(score, timeName, label)
          if (isDate) {
            objectPath.set(score, dateName, moment().format('YYYY-MM-DD'))
          }
          scores.push(score)
        })
      } else {
        let score = cloneDeep(scoreValues)
        if (isDate) {
          objectPath.set(score, dateName, moment().format('YYYY-MM-DD'))
        }
        scores.push(score)
      }
      setScores(scores)
    } else {
      let scores = cloneDeep(objectPath.get(scoreList, scoreIndex))
      setScores(scores)
    }
  }, [scoreIndex])

  useEffect(() => {
    if (scoreActionCreateOnly?.isSingleData) {
      setScores(scoreList)
    }
  }, [scoreList])

  const checkCalculate = ({ scores, index, key }) => {
    let mapKey = mapCalculate[key]
    if (!mapKey) return scores
    Object.keys(mapKey).forEach((resultKey) => {
      let resultFormula = mapKey[resultKey]
      if (!resultFormula) return
      let setting = fullConfig.find(({ key }) => key === resultKey)
      let dataValue = scores[index]
      let calValue = calculateValue({ dataValue, setting, formData })
      objectPath.set(scores, [index, setting.name], calValue)
    })
    return scores
  }

  const handleChangeTextField = (index, key, name, value) => {
    if (isDate && !objectPath.has(scores, [index, dateName])) {
      objectPath.set(scores, [index, dateName], dateTime)
    }
    if (times && !objectPath.has(scores, [index, timeName])) {
      let time = objectPath.get(times, index)
      objectPath.set(scores, [index, timeName], time.label)
    }
    objectPath.set(scores, [index, name], value)
    let resultScores = checkCalculate({ scores, index, key })
    setScores(resultScores)
    setRender(true)
    if (isCreateOnly) {
      saveScore(index, resultScores, isCreateOnly)
    }
  }

  const updateDate = (date) => {
    scores.forEach((score, index) => {
      handleChangeTextField(
        index,
        dateKey,
        dateName,
        moment(date).format('YYYY-MM-DD')
      )
    })
  }

  const [render, setRender] = useState(false)

  useEffect(() => {
    if (render) {
      setRender(false)
    }
  }, [render])

  const dateTime = useMemo(() => {
    let foundDate = scores.find((item) => objectPath.get(item, dateName))
    if (foundDate) {
      return objectPath.get(foundDate, dateName)
    }
    return null
  }, [scores])

  const timeFormat = 'HH:mm'
  const [timeValue, setTimeValue] = useState('')
  const [showPickerIndex, setShowPickerIndex] = useState(false)

  const convertTime = (time, format = timeFormat) => {
    if (time) {
      const date = dayjs(time)
      if (date.isValid()) {
        return date.format(format)
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  const handleChangeTime = (newValue, index = showPickerIndex) => {
    let dateDefault = dayjs().format('YYYY-MM-DD')
    let timeDefault = convertTime(`${dateDefault} ${newValue}`)
    setTimeValue(newValue)
    handleChangeTextField(index, timeKey, timeName, timeDefault)
  }

  const handleTogglePicker = (index) => {
    if (index >= 0) {
      setShowPickerIndex(index)
    } else {
      setShowPickerIndex(false)
    }
  }

  const deleteTimeToScoreChart = (index) => {
    objectPath.set(scores, `${index}.deleteMode`, 1)
    setScores(scores)
    setRender(true)
    setShowPickerIndex(false)
  }

  const timePicker = (time, index) => {
    return (
      <Popover className="popover-timepicker">
        <Popover.Content>
          <Row>
            <Col sm="12">
              <TimePickerCustom
                value={time}
                onChange={(value) => handleChangeTime(value, index)}
                onClose={handleTogglePicker}
                formatDisplay={timeFormat}
                timeValue={timeValue}
                setTimeValue={setTimeValue}
                convertTime={convertTime}
              />
            </Col>
            <Col
              sm="12"
              className="d-flex align-items-center justify-content-between mt-2"
            >
              <Button
                variant={'outline-danger'}
                className="px-2"
                onClick={() => deleteTimeToScoreChart(index)}
              >
                <FeatherIcon className={'mr-1'} icon="trash" size="18" />
                ลบเวลา
              </Button>
              <ManualTimeInput
                value={time || ''}
                onChange={(value) => handleChangeTime(value, index)}
                onConfirm={() => handleTogglePicker()}
                onPreview={(value) => setTimeValue(value)}
              />
            </Col>
          </Row>
        </Popover.Content>
      </Popover>
    )
  }

  const addTimeToScoreChart = () => {
    if (!isCurrentTime) {
      let score = cloneDeep(scoreValues)
      if (isFrequency) {
        objectPath.set(score, timeName, '')
      } else {
        objectPath.set(score, timeName, moment().format(timeFormat))
      }

      if (isDate) {
        let date = objectPath.get(scores, [0, dateName])
        objectPath.set(score, dateName, date ?? moment().format('YYYY-MM-DD'))
      }
      scores.push({ ...score, isTimeAdd: true })
      setScores(scores)
      setRender(true)
    }
  }

  const focusScoreInput = (rowIndex, colIndex) => {
    let input = addScoreRef.current?.querySelector(
      `[data-score-row="${rowIndex}"][data-score-col="${colIndex}"] input`
    )
    if (input) {
      input.focus()
      return true
    }
    return false
  }

  const handleKeyDownTextField = (e, rowIndex, colIndex) => {
    if (e.key === (scoreSetting.addScoreNextInputKey ?? 'Enter')) {
      e.preventDefault()
      if (!focusScoreInput(rowIndex, colIndex + 1)) {
        focusScoreInput(rowIndex + 1, 0)
      }
    } else if (
      e.key === (scoreSetting.addScorePreviousRowKey ?? 'ArrowUp')
    ) {
      e.preventDefault()
      focusScoreInput(rowIndex - 1, colIndex)
    } else if (e.key === (scoreSetting.addScoreNextRowKey ?? 'ArrowDown')) {
      e.preventDefault()
      focusScoreInput(rowIndex + 1, colIndex)
    } else if (e.key === (scoreSetting.addScoreLeftKey ?? 'ArrowLeft')) {
      e.preventDefault()
      focusScoreInput(rowIndex, colIndex - 1)
    } else if (e.key === (scoreSetting.addScoreRightKey ?? 'ArrowRight')) {
      e.preventDefault()
      focusScoreInput(rowIndex, colIndex + 1)
    }
  }

  let scoreRowIndex = 0
  const getScoreInputRowIndex = () => scoreRowIndex++

  return (
    <div ref={addScoreRef}>
      <div
        className={`border border-${scoreIndex === -1 ? 'success' : 'warning'}`}
      >
        {isDate && (
          <div className="body-box d-flex">
            <div className="d-flex border w-100 p-2">
              <DatePickerCustom
                id={`date-${fieldKey}`}
                value={dateTime}
                onChange={(date) => updateDate(date.value)}
                formatDisplay={dateChart?.date?.format ?? 'DD/MM/YYYY'}
                placeholder={dateChart?.date?.format ?? 'DD/MM/YYYY'}
                isClearable={true}
                maskSign={dateChart?.date?.maskSign ?? '/'}
                calendar={dateChart?.date?.calendar}
              />
              <div className="d-flex align-items-center pl-3">
                <FeatherIcon
                  onClick={addTimeToScoreChart}
                  className={'text-success add-time-button'}
                  icon="plus-square"
                  size="24"
                />
              </div>
            </div>
          </div>
        )}
        {!isCurrentTime ? (
          <>
            <div className="d-flex">
              {scores.map((score, index) => {
                let deleteMode = objectPath.get(score, 'deleteMode')
                if (deleteMode) return ''
                let scoreValue = objectPath.get(score, [timeName])
                let isTimeAdd = objectPath.get(score, 'isTimeAdd')

                if (!isFrequency && showPickerIndex === index) {
                  return (
                    <OverlayTrigger
                      trigger="click"
                      placement="bottom"
                      overlay={timePicker(scoreValue, index)}
                      rootClose={true}
                      show={showPickerIndex === index} // Controlled by state
                      onToggle={(nextShow) => setShowPickerIndex(nextShow)}
                    >
                      <div
                        className="sub-box"
                        style={{ ...scoreValueStyle, color: colorByTheme }}
                        onClick={() => handleTogglePicker(index)}
                      >
                        {scoreValue}
                      </div>
                    </OverlayTrigger>
                  )
                }
                if (isFrequency && isTimeAdd) {
                  let isWarning = checkIsWarning(
                    warningChart,
                    timeName,
                    scoreValue
                  )
                  return (
                    <div
                      className="sub-box"
                      style={{ ...scoreValueStyle, color: colorByTheme }}
                    >
                      <TextField
                        id={`time-frequency-${index}`}
                        inputProps={{
                          style: {
                            ...inputStyles,
                            color: isWarning ? 'red' : colorByTheme,
                          },
                        }}
                        autoComplete="off"
                        value={scoreValue}
                        onChange={(e) => {
                          handleChangeTextField(
                            index,
                            timeKey,
                            timeName,
                            e.target.value
                          )
                        }}
                      />
                    </div>
                  )
                }
                return (
                  <div
                    className="sub-box"
                    style={{ ...scoreValueStyle, color: colorByTheme }}
                    onClick={() => handleTogglePicker(index)}
                  >
                    {scoreValue}
                  </div>
                )
              })}
            </div>
            {scoreConfig.map((config) => {
              let { group, key, name, data, enabled } = config
              if (!enabled) return
              let rowIndex = group ? null : getScoreInputRowIndex()
              let rowIndexes = group
                ? data.map(({ enabled }) =>
                    enabled ? getScoreInputRowIndex() : null
                  )
                : []
              return (
                <div className="d-flex">
                  {scores.map((score, index) => {
                    let deleteMode = objectPath.get(score, ['deleteMode'])
                    if (deleteMode) return ''
                    let timeValue = objectPath.get(score, timeName)
                    let scoreValue = objectPath.get(score, [name])
                    let isWarning = checkIsWarning(
                      warningChart,
                      name,
                      scoreValue
                    )
                    if (group) {
                      return (
                        <div>
                          {data.map(({ name, key, enabled }, dataIndex) => {
                            if (!enabled) return
                            let scoreValue = objectPath.get(scores, [
                              index,
                              name,
                            ])
                            let isWarning = checkIsWarning(
                              warningChart,
                              name,
                              scoreValue
                            )
                            let rowIndex = rowIndexes[dataIndex]
                            let colIndex = index
                            return (
                              <div
                                className="sub-box"
                                style={scoreValueStyle}
                                data-score-row={rowIndex}
                                data-score-col={colIndex}
                              >
                                <TextField
                                  id={`${fieldKey}-group-${config.name}-${timeValue}-${name}-${index}`}
                                  inputProps={{
                                    style: {
                                      ...inputStyles,
                                      color: isWarning ? 'red' : colorByTheme,
                                    },
                                  }}
                                  autoComplete="off"
                                  value={scoreValue}
                                  onChange={(e) => {
                                    handleChangeTextField(
                                      index,
                                      key,
                                      name,
                                      e.target.value
                                    )
                                  }}
                                  onKeyDown={(e) =>
                                    handleKeyDownTextField(
                                      e,
                                      rowIndex,
                                      colIndex
                                    )
                                  }
                                />
                              </div>
                            )
                          })}
                        </div>
                      )
                    }
                    let colIndex = index
                    return (
                      <div
                        className="sub-box"
                        style={scoreValueStyle}
                        data-score-row={rowIndex}
                        data-score-col={colIndex}
                      >
                        <TextField
                          id={`${fieldKey}-time-${name}-${index}`}
                          inputProps={{
                            style: {
                              ...inputStyles,
                              color: isWarning ? 'red' : colorByTheme,
                            },
                          }}
                          autoComplete="off"
                          value={scoreValue}
                          onChange={(e) =>
                            handleChangeTextField(
                              index,
                              key,
                              name,
                              e.target.value
                            )
                          }
                          onKeyDown={(e) =>
                            handleKeyDownTextField(e, rowIndex, colIndex)
                          }
                        />
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </>
        ) : (
          <>
            {scoreConfig.map((config, index) => {
              let { group, key, name, data, enabled } = config
              let scoreValue = objectPath.get(scores, [index, name])
              let isWarning = checkIsWarning(warningChart, name, scoreValue)
              if (!enabled) return
              let rowIndex = group ? null : getScoreInputRowIndex()
              let rowIndexes = group
                ? data.map(({ enabled }) =>
                    enabled ? getScoreInputRowIndex() : null
                  )
                : []
              if (group) {
                return (
                  <div>
                    {data.map(({ key, name, enabled }, dataIndex) => {
                      if (!enabled) return
                      let scoreValue = objectPath.get(scores, [index, name])
                      let isWarning = checkIsWarning(
                        warningChart,
                        name,
                        scoreValue
                      )
                      let rowIndex = rowIndexes[dataIndex]
                      let colIndex = 0
                      return (
                        <div
                          className="sub-box"
                          style={scoreValueStyle}
                          data-score-row={rowIndex}
                          data-score-col={colIndex}
                        >
                          <TextField
                            id={`${fieldKey}-group-${config.name}-${name}-${index}`}
                            inputProps={{
                              style: {
                                ...inputStyles,
                                color: isWarning ? 'red' : colorByTheme,
                              },
                            }}
                            autoComplete="off"
                            value={scoreValue}
                            onChange={(e) => {
                              handleChangeTextField(
                                index,
                                key,
                                name,
                                e.target.value
                              )
                            }}
                            onKeyDown={(e) =>
                              handleKeyDownTextField(e, rowIndex, colIndex)
                            }
                          />
                        </div>
                      )
                    })}
                  </div>
                )
              }
              let colIndex = 0
              return (
                <div
                  className="sub-box"
                  style={scoreValueStyle}
                  data-score-row={rowIndex}
                  data-score-col={colIndex}
                >
                  <TextField
                    id={`${fieldKey}-time-${name}-${index}`}
                    inputProps={{
                      style: {
                        ...inputStyles,
                        color: isWarning ? 'red' : colorByTheme,
                      },
                    }}
                    autoComplete="off"
                    value={scoreValue}
                    onChange={(e) =>
                      handleChangeTextField(index, key, name, e.target.value)
                    }
                    onKeyDown={(e) =>
                      handleKeyDownTextField(e, rowIndex, colIndex)
                    }
                  />
                </div>
              )
            })}
          </>
        )}
      </div>

      <div className="d-flex align-items-end justify-content-end">
        {isCreateOnly ? (
          <div className="d-flex">
            {scoreActionCreateOnly.isOnAPI ? (
              <div className="mb-2">
                <Button
                  id={`${fieldKey}-save-api`}
                  className="score-button"
                  variant="outline-success"
                  onClick={() => saveScore(scoreIndex, scores)}
                >
                  <FeatherIcon icon="check" size="16" />
                </Button>
              </div>
            ) : (
              ''
            )}
          </div>
        ) : (
          <div className="d-flex">
            <div className="mb-2">
              <Button
                id={`${fieldKey}-save`}
                className="score-button"
                variant="outline-success"
                onClick={() => saveScore(scoreIndex, scores)}
              >
                <FeatherIcon icon="check" size="16" />
              </Button>
            </div>
            <div className="mx-1">
              <Button
                id={`${fieldKey}-cancel`}
                className="score-button"
                variant="outline-danger"
                onClick={() => cancelScore()}
              >
                <FeatherIcon icon="x" size="16" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
export default AddScore
